import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const ACTION_LABELS = {
  PRODUCT_ADDED: { icon: '➕', label: 'Product Added', color: '#16a34a' },
  PRODUCT_REMOVED: { icon: '➖', label: 'Product Removed', color: '#dc2626' },
  QUANTITY_UPDATED: { icon: '🔄', label: 'Qty Updated', color: '#2563eb' },
  DISCOUNT_APPLIED: { icon: '🏷️', label: 'Discount Applied', color: '#7c3aed' },
  DISCOUNT_REMOVED: { icon: '🏷️', label: 'Discount Removed', color: '#9ca3af' },
  GIFT_CARD_APPLIED: { icon: '🎁', label: 'Gift Card Applied', color: '#0891b2' },
  GIFT_CARD_REMOVED: { icon: '🎁', label: 'Gift Card Removed', color: '#9ca3af' },
  CART_CLEARED: { icon: '🗑️', label: 'Cart Cleared', color: '#f59e0b' },
  ORDER_PLACED: { icon: '✅', label: 'Order Placed', color: '#16a34a' },
  ORDER_CANCELLED: { icon: '❌', label: 'Order Cancelled', color: '#dc2626' },
  CUSTOMER_SELECTED: { icon: '👤', label: 'Customer Selected', color: '#6366f1' },
  CUSTOMER_REMOVED: { icon: '👤', label: 'Customer Removed', color: '#9ca3af' },
  PAYMENT_INITIATED: { icon: '💳', label: 'Payment Started', color: '#f59e0b' },
  PAYMENT_SUCCESS: { icon: '💳', label: 'Payment Success', color: '#16a34a' },
  PAYMENT_FAILED: { icon: '💳', label: 'Payment Failed', color: '#dc2626' },
  PAYMENT_TIMEOUT: { icon: '⏱️', label: 'Payment Timeout', color: '#ea580c' },
  PAYMENT_CANCELLED: { icon: '🚫', label: 'Payment Cancelled', color: '#9ca3af' },
  LOGIN: { icon: '🔑', label: 'Login', color: '#2563eb' },
  LOGOUT: { icon: '🚪', label: 'Logout', color: '#6b7280' }
};

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
}

function formatDetails(action, details) {
  switch (action) {
    case 'PRODUCT_ADDED': {
      const loc = details.locationType === 'warehouse' ? ' 🏭 Warehouse' : details.locationType === 'store' ? ' 🏪 In-Store' : '';
      const dims = details.variant ? ` / ${details.variant}` : '';
      return `${details.title}${dims} × ${details.quantity} — ₹${details.price}${loc}`;
    }
    case 'PRODUCT_REMOVED':
      return details.title ? `${details.title}${details.variant ? ` (${details.variant})` : ''}` : (details.sku ? `SKU: ${details.sku}` : 'Item removed');
    case 'QUANTITY_UPDATED': {
      const name = details.title || details.sku || 'Item';
      const dims = details.variant ? ` / ${details.variant}` : '';
      const oldQ = details.oldQty ?? '?';
      const newQ = details.newQty ?? details.quantity ?? '?';
      return `${name}${dims} — ${oldQ} → ${newQ}`;
    }
    case 'DISCOUNT_APPLIED':
      return `${details.code}${details.amount ? ` (₹${details.amount})` : ''}`;
    case 'DISCOUNT_REMOVED':
      return details.code;
    case 'GIFT_CARD_APPLIED':
      return `****${details.code}${details.balance ? ` (Balance: ₹${details.balance})` : ''}`;
    case 'GIFT_CARD_REMOVED':
      return `****${details.code}`;
    case 'ORDER_PLACED': {
      const orderType = details.fulfillmentSource === 'warehouse' ? ' 🏭 Warehouse' : details.fulfillmentSource === 'store' ? ' 🏪 In-Store' : '';
      return `#${details.orderName} — ₹${details.total} via ${details.paymentMethod}${orderType}`;
    }
    case 'ORDER_CANCELLED':
      return `${details.orderName || ''} — ${details.error || 'Cancelled'}`;
    case 'CUSTOMER_SELECTED':
      return `${details.name}${details.phone ? ` (${details.phone})` : ''}`;
    case 'PAYMENT_INITIATED':
      return `${details.method} — ₹${details.amount}${details.refNumber ? ` [${details.refNumber}]` : ''}`;
    case 'PAYMENT_SUCCESS':
      return `${details.method}${details.txnId ? ` (${details.txnId})` : ''}${details.refNumber ? ` [${details.refNumber}]` : ''}`;
    case 'PAYMENT_FAILED':
      return `${details.method} — ${details.error || 'Failed'}${details.refNumber ? ` [${details.refNumber}]` : ''}`;
    case 'PAYMENT_TIMEOUT':
      return `${details.method} — Timed out${details.amount ? ` (₹${details.amount})` : ''}${details.refNumber ? ` [${details.refNumber}]` : ''}`;
    case 'PAYMENT_CANCELLED':
      return `${details.method} — Cancelled${details.amount ? ` (₹${details.amount})` : ''}${details.refNumber ? ` [${details.refNumber}]` : ''}`;
    case 'LOGIN':
      return `${details.username}${details.storeName ? ` @ ${details.storeName}` : ''}`;
    default:
      return '';
  }
}

// Shared log list component used by both drawer and page
export function ActivityLogContent({ mode = 'drawer', onClose }) {
  const isPage = mode === 'page';
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  // Page defaults to last 7 days; the compact drawer keeps its latest-100 view.
  const [dateRange, setDateRange] = useState(isPage ? '7days' : 'all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [staff, setStaff] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1, page: 1, pageSize: 100 });
  const [exporting, setExporting] = useState(false);
  const useCustom = dateRange === 'custom' && customFrom && customTo;

  const buildParams = useCallback((extra = {}) => {
    const params = { filter, ...extra };
    if (useCustom) { params.from = customFrom; params.to = customTo; }
    // Always send dateRange (incl. 'all') — the server defaults a MISSING
    // dateRange to '7days', so omitting it for 'all' silently queried 7 days.
    else params.dateRange = dateRange;
    if (staff) params.staff = staff;
    return params;
  }, [filter, dateRange, useCustom, customFrom, customTo, staff]);

  const fetchLogs = useCallback(async (goPage = page) => {
    if (dateRange === 'custom' && !(customFrom && customTo)) return; // wait for both dates
    setLoading(true);
    try {
      const params = buildParams({ page: goPage, pageSize: isPage ? 100 : 100 });
      const res = await api.get('/events/activity-feed', { params });
      if (res.data.success) {
        setLogs(res.data.logs);
        setMeta({ total: res.data.total || 0, totalPages: res.data.totalPages || 1, page: res.data.page || goPage, pageSize: res.data.pageSize || 100 });
      }
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLoading(false);
    }
  }, [buildParams, page, isPage, dateRange, customFrom, customTo]);

  // Single source of fetching: any filter change or page move refetches. Filter
  // controls reset page→1 (below), so changing a filter and the page never
  // double-fires — this effect runs once per intentional change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLogs(page); }, [page, filter, dateRange, customFrom, customTo, staff]);

  // Load the staff dropdown once (page mode only).
  useEffect(() => {
    if (!isPage) return;
    api.get('/events/activity-feed/staff').then(r => setStaffList(r.data.staff || [])).catch(() => {});
  }, [isPage]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/events/activity-feed/export', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const tag = useCustom ? (customFrom === customTo ? customFrom : `${customFrom}_${customTo}`) : dateRange;
      a.download = `activity-log-${tag}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
    setExporting(false);
  };

  if (!isPage) {
    // --- Drawer mode (compact) ---
    return (
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px', background: 'white', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)', zIndex: 9999, display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e2e8f0' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>Activity Log</span>
            <span style={{ fontSize: '11px', background: '#e2e8f0', padding: '2px 8px', borderRadius: '10px', fontWeight: '600', color: '#475569' }}>{logs.length}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={fetchLogs} style={{ fontSize: '11px', padding: '4px 10px', background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>{loading ? '...' : '↻ Refresh'}</button>
            <button onClick={onClose} style={{ fontSize: '18px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}>✕</button>
          </div>
        </div>
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '6px' }}>
          {[['all', 'All'], ['cart', 'Cart'], ['orders', 'Orders'], ['payments', 'Payments']].map(([key, label]) => (
            <button key={key} onClick={() => { setPage(1); setFilter(key); }} style={{ fontSize: '11px', padding: '4px 12px', borderRadius: '14px', border: 'none', cursor: 'pointer', fontWeight: '600', background: filter === key ? '#1e293b' : '#f1f5f9', color: filter === key ? 'white' : '#64748b' }}>{label}</button>
          ))}
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
          {logs.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', marginTop: '40px' }}>{loading ? 'Loading...' : 'No activity recorded yet'}</div>
          ) : logs.map((log) => {
            const meta = ACTION_LABELS[log.action] || { icon: '•', label: log.action, color: '#6b7280' };
            const detailText = formatDetails(log.action, log.details);
            return (
              <div key={log.id} style={{ padding: '8px 10px', marginBottom: '4px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px' }}>{meta.icon}</span>
                    <span style={{ fontSize: '11px', fontWeight: '700', color: meta.color }}>{meta.label}</span>
                  </div>
                  <span style={{ fontSize: '10px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{formatTime(log.timestamp)}</span>
                </div>
                {detailText && <div style={{ fontSize: '12px', color: '#475569', marginLeft: '22px', lineHeight: '1.4' }}>{detailText}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // --- Page mode (table layout) ---
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Activity Log</span>
            <span style={{ fontSize: '12px', background: '#e2e8f0', padding: '3px 10px', borderRadius: '10px', fontWeight: '600', color: '#475569' }}>{meta.total.toLocaleString('en-IN')} events</span>
            <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
              {[['all', 'All'], ['cart', 'Cart'], ['orders', 'Orders'], ['payments', 'Payments']].map(([key, label]) => (
                <button key={key} onClick={() => { setPage(1); setFilter(key); }} style={{ fontSize: '12px', padding: '5px 14px', borderRadius: '6px', border: '1px solid', cursor: 'pointer', fontWeight: '600', background: filter === key ? '#1e293b' : 'white', color: filter === key ? 'white' : '#64748b', borderColor: filter === key ? '#1e293b' : '#e2e8f0' }}>{label}</button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button onClick={() => fetchLogs(page)} style={{ fontSize: '12px', padding: '6px 14px', background: 'white', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>{loading ? 'Loading…' : '↻ Refresh'}</button>
            <button onClick={exportCsv} disabled={exporting || !logs.length} style={{ fontSize: '12px', padding: '6px 14px', background: exporting || !logs.length ? '#94a3b8' : '#0369a1', color: 'white', border: 'none', borderRadius: '6px', cursor: exporting || !logs.length ? 'not-allowed' : 'pointer', fontWeight: '600' }}>{exporting ? 'Exporting…' : '↓ Export CSV'}</button>
          </div>
        </div>
        {/* Filters row: staff + date range (+ custom dates) */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={staff} onChange={e => { setPage(1); setStaff(e.target.value); }} style={{ fontSize: '13px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', color: '#374151', maxWidth: '220px' }}>
            <option value="">All staff</option>
            {staffList.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={dateRange} onChange={e => { setPage(1); setDateRange(e.target.value); }} style={{ fontSize: '13px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', color: '#374151' }}>
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="all">All Time</option>
            <option value="custom">Custom range…</option>
          </select>
          {dateRange === 'custom' && (
            <>
              <input type="date" value={customFrom} max={customTo || undefined} onChange={e => { setPage(1); setCustomFrom(e.target.value); }} style={{ fontSize: '13px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', color: '#374151' }} />
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>→</span>
              <input type="date" value={customTo} min={customFrom || undefined} onChange={e => { setPage(1); setCustomTo(e.target.value); }} style={{ fontSize: '13px', padding: '5px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', color: '#374151' }} />
              {!(customFrom && customTo) && <span style={{ fontSize: '12px', color: '#dc2626' }}>Pick both dates</span>}
            </>
          )}
        </div>
      </div>

      {/* Table header */}
      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 120px 160px', padding: '10px 24px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
        <div>Event</div>
        <div>Details</div>
        <div>Staff</div>
        <div style={{ textAlign: 'right' }}>Time</div>
      </div>

      {/* Table body */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', marginTop: '60px' }}>{loading ? 'Loading events...' : 'No activity recorded'}</div>
        ) : logs.map((log, idx) => {
          const am = ACTION_LABELS[log.action] || { icon: '•', label: log.action, color: '#6b7280' };
          const detailText = formatDetails(log.action, log.details);
          return (
            <div key={log.id} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 120px 160px', padding: '10px 24px', borderBottom: '1px solid #f1f5f9', alignItems: 'center', background: idx % 2 === 0 ? 'white' : '#fafbfc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px' }}>{am.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: '700', color: am.color }}>{am.label}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '12px' }} title={detailText}>
                {detailText || '—'}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280' }}>{log.user || '—'}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {new Date(log.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination footer */}
      <div style={{ padding: '10px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px', color: '#64748b' }}>
        <span>
          {meta.total === 0 ? 'No events' : `Showing ${((meta.page - 1) * meta.pageSize) + 1}–${Math.min(meta.page * meta.pageSize, meta.total)} of ${meta.total.toLocaleString('en-IN')}`}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button disabled={meta.page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))}
            style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', color: meta.page <= 1 ? '#cbd5e1' : '#334155', cursor: meta.page <= 1 ? 'not-allowed' : 'pointer', fontWeight: '600' }}>← Prev</button>
          <span style={{ fontWeight: '600', color: '#334155' }}>Page {meta.page} of {meta.totalPages}</span>
          <button disabled={meta.page >= meta.totalPages || loading} onClick={() => setPage(p => p + 1)}
            style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', color: meta.page >= meta.totalPages ? '#cbd5e1' : '#334155', cursor: meta.page >= meta.totalPages ? 'not-allowed' : 'pointer', fontWeight: '600' }}>Next →</button>
        </div>
      </div>
    </div>
  );
}

// Drawer wrapper (default export for backward compat)
export default function ActivityLog({ isOpen, onClose }) {
  if (!isOpen) return null;
  return <ActivityLogContent mode="drawer" onClose={onClose} />;
}
